import React, { useEffect, useRef, useState } from "react";
import { API } from "../services/api";
import "../features/livreur/Livreur.css";

function extensionForContentType(contentType) {
    const normalized = String(contentType || "").toLowerCase();
    if (normalized.includes("png")) return "png";
    if (normalized.includes("webp")) return "webp";
    if (normalized.includes("gif")) return "gif";
    if (normalized.includes("bmp")) return "bmp";
    if (normalized.includes("svg")) return "svg";
    return "jpg";
}

function downloadPhoto(photo, historiqueId) {
    if (!photo?.url) return;
    const link = document.createElement("a");
    link.href = photo.url;
    link.download = `bon-livraison-signe-${historiqueId}-photo-${photo.index + 1}.${extensionForContentType(photo.contentType)}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

function getSelectionLabel(selectedCount, availableCount, canDelete) {
    const plural = selectedCount > 1 ? "s" : "";
    if (selectedCount > 0) return `${selectedCount} image${plural} cochée${plural} pour suppression`;
    if (availableCount > 1) return `Cliquez sur une vignette pour l’afficher en grand${canDelete ? " · utilisez la coche pour supprimer" : ""}`;
    if (availableCount === 0) return "";
    const deletionHint = canDelete ? " · utilisez la coche pour supprimer" : "";
    return `Photo affichée en grand format${deletionHint}`;
}

export default function SignedBonPhotoModal({ historiqueId, title = "Photos de la livraison", photoCount = 0, onClose, onAddFiles = null, onDeleteMany = null }) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [working, setWorking] = useState(false);
    const dialogRef = useRef(null);

    useEffect(() => {
        let active = true;
        const objectUrls = [];
        const count = Math.max(0, photoCount || 0);
        setSelected(new Set());
        setActiveIndex(null);
        setLoading(count > 0);
        setPhotos([]);
        Promise.all(Array.from({ length: count }, async (_, index) => {
            const path = index === 0 ? `/bon-livraison/livreur/historique/${historiqueId}/photo` : `/bon-livraison/livreur/historique/${historiqueId}/photo/${index}`;
            try {
                const response = await API.get(path, { responseType: "blob", noRedirectOn401: true });
                const url = URL.createObjectURL(response.data);
                objectUrls.push(url);
                return { index, url, contentType: response.data?.type };
            } catch {
                return { index, url: null, contentType: null };
            }
        })).then(items => {
            if (!active) return;
            setPhotos(items);
            setActiveIndex(items.find(item => item.url)?.index ?? null);
        }).finally(() => active && setLoading(false));
        return () => {
            active = false;
            objectUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [historiqueId, photoCount]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return undefined;
        const closeFromBackdrop = event => {
            if (event.target === dialog) onClose();
        };
        const closeFromKeyboard = event => {
            if (event.key === "Escape") onClose();
        };
        dialog.addEventListener("click", closeFromBackdrop);
        dialog.addEventListener("keydown", closeFromKeyboard);
        return () => {
            dialog.removeEventListener("click", closeFromBackdrop);
            dialog.removeEventListener("keydown", closeFromKeyboard);
        };
    }, [onClose]);

    const toggleSelected = index => setSelected(current => {
        const next = new Set(current);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
    });

    const handleFiles = async event => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!onAddFiles || files.length === 0) return;
        setWorking(true);
        try { await onAddFiles(files); } catch { /* Le parent affiche l'erreur. */ } finally { setWorking(false); }
    };

    const deleteSelected = async () => {
        if (!onDeleteMany || selected.size === 0) return;
        setWorking(true);
        try {
            await onDeleteMany([...selected].sort((a, b) => a - b));
            setSelected(new Set());
            setConfirmDelete(false);
        } catch { /* Le parent affiche l'erreur. */ } finally { setWorking(false); }
    };

    const availablePhotos = photos.filter(photo => photo.url);
    const activePhoto = availablePhotos.find(photo => photo.index === activeIndex) || availablePhotos[0] || null;
    const activePosition = activePhoto ? availablePhotos.findIndex(photo => photo.index === activePhoto.index) : -1;
    const selectionLabel = getSelectionLabel(selected.size, availablePhotos.length, Boolean(onDeleteMany));

    const showRelativePhoto = offset => {
        if (availablePhotos.length < 2 || activePosition < 0) return;
        const nextPosition = (activePosition + offset + availablePhotos.length) % availablePhotos.length;
        setActiveIndex(availablePhotos[nextPosition].index);
    };

    return (
        <dialog ref={dialogRef} open className="livreur-photo-modal" aria-label={title} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(12, 59, 60, 0.58)", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", maxWidth: "none", maxHeight: "none", margin: 0, padding: "20px", border: 0 }}>
            <div className="livreur-photo-modal__sheet" style={{ width: "min(1040px, 96vw)", maxHeight: "94vh", background: "white", borderRadius: "20px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div className="livreur-photo-modal__header" style={{ padding: "16px 18px", display: "flex", alignItems: "center", borderBottom: "1px solid #D8D2C6" }}>
                    <div style={{ flex: 1 }}>
                        <strong style={{ color: "#122A26", fontFamily: '"Fraunces", Georgia, serif', fontSize: "20px" }}>{title}</strong>
                        <div style={{ color: "#5A6D68", fontSize: "12px", marginTop: "2px" }}>{selectionLabel}</div>
                    </div>
                    <button type="button" aria-label="Fermer" onClick={onClose} style={{ border: 0, background: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}>✕</button>
                </div>

                <div className="livreur-photo-modal__content" style={{ minHeight: "240px", padding: "16px", overflowY: "auto", background: "#F4F1EA" }}>
                    {loading && <div style={{ color: "#94a3b8", textAlign: "center", padding: "48px" }}>Chargement…</div>}
                    {!loading && photos.length === 0 && <div style={{ color: "#64748b", textAlign: "center", padding: "48px 16px" }}>Aucune photo pour cette livraison.</div>}
                    {!loading && activePhoto && <>
                        <div className="livreur-photo-viewer">
                            <img src={activePhoto.url} alt={availablePhotos.length === 1 ? "Bon de livraison signé" : `Bon de livraison signé — image ${activePhoto.index + 1}`} className="livreur-photo-viewer__image" />
                            {availablePhotos.length > 1 && <>
                                <button type="button" className="livreur-photo-viewer__nav livreur-photo-viewer__nav--previous" aria-label="Photo précédente" onClick={() => showRelativePhoto(-1)}>‹</button>
                                <button type="button" className="livreur-photo-viewer__nav livreur-photo-viewer__nav--next" aria-label="Photo suivante" onClick={() => showRelativePhoto(1)}>›</button>
                                <span className="livreur-photo-viewer__counter" aria-live="polite">{activePosition + 1} / {availablePhotos.length}</span>
                            </>}
                        </div>

                        {(photos.length > 1 || onDeleteMany) && <div className="livreur-photo-thumbnails" aria-label="Choisir la photo à afficher">
                            {photos.map(photo => {
                                const isActive = photo.index === activePhoto.index;
                                const isSelected = selected.has(photo.index);
                                return <div key={photo.index} className={`livreur-photo-thumbnail${isActive ? " livreur-photo-thumbnail--active" : ""}`}>
                                    <button type="button" className="livreur-photo-thumbnail__preview" aria-label={`Afficher l’image ${photo.index + 1} en grand`} aria-current={isActive ? "true" : undefined} onClick={() => photo.url && setActiveIndex(photo.index)} disabled={!photo.url}>
                                        {photo.url ? <img src={photo.url} alt="" draggable="false" /> : <span>Indisponible</span>}
                                        <span className="livreur-photo-thumbnail__number">{photo.index + 1}</span>
                                    </button>
                                    {onDeleteMany && photo.url && <button type="button" className={`livreur-photo-thumbnail__select${isSelected ? " livreur-photo-thumbnail__select--active" : ""}`} aria-label={`${isSelected ? "Décocher" : "Cocher"} l’image ${photo.index + 1} pour suppression`} aria-pressed={isSelected} onClick={() => toggleSelected(photo.index)}>✓</button>}
                                </div>;
                            })}
                        </div>}
                    </>}
                    {!loading && photos.length > 0 && !activePhoto && <div style={{ color: "#b91c1c", textAlign: "center", padding: "48px 16px" }}>Les photos sont temporairement indisponibles.</div>}
                </div>

                {(availablePhotos.length > 0 || onAddFiles || (onDeleteMany && selected.size > 0)) && <div className="livreur-photo-modal__actions" style={{ padding: "12px 18px", display: "flex", gap: "10px", flexWrap: "wrap", borderTop: "1px solid #D8D2C6" }}>
                    {activePhoto && <button type="button" aria-label="Télécharger la photo affichée" onClick={() => downloadPhoto(activePhoto, historiqueId)} disabled={working} style={{ flex: "1 1 190px", borderRadius: "8px", padding: "10px", background: "#F4F1EA", color: "#0C3B3C", border: "1px solid #0C3B3C", cursor: working ? "not-allowed" : "pointer", fontWeight: 700 }}>
                        ↓ Télécharger la photo affichée
                    </button>}
                    {onAddFiles && <>
                        <label style={{ flex: "1 1 160px", textAlign: "center", borderRadius: "8px", padding: "10px", background: "#0C3B3C", color: "#F4F1EA", cursor: working ? "not-allowed" : "pointer", fontWeight: 700 }}>Prendre une photo<input aria-label="Prendre une photo" type="file" accept="image/*" capture="environment" onChange={handleFiles} disabled={working} style={{ display: "none" }} /></label>
                        <label style={{ flex: "1 1 180px", textAlign: "center", borderRadius: "8px", padding: "10px", background: "white", color: "#0C3B3C", border: "1px solid #0C3B3C", cursor: working ? "not-allowed" : "pointer", fontWeight: 700 }}>Importer depuis la galerie<input aria-label="Importer depuis la galerie" type="file" accept="image/*" multiple onChange={handleFiles} disabled={working} style={{ display: "none" }} /></label>
                    </>}
                    {onDeleteMany && selected.size > 0 && <button type="button" onClick={() => setConfirmDelete(true)} disabled={working} style={{ flex: "1 1 150px", borderRadius: "8px", padding: "10px", background: "#fff", color: "#b91c1c", border: "1px solid #dc2626", cursor: "pointer", fontWeight: 700 }}>Supprimer ({selected.size})</button>}
                </div>}
            </div>

            {confirmDelete && <div role="alertdialog" aria-modal="true" aria-label="Confirmer la suppression" style={{ position: "fixed", inset: 0, zIndex: 1010, background: "rgba(15,23,42,0.62)", display: "grid", placeItems: "center", padding: "24px" }}>
                <div style={{ width: "min(420px, 94vw)", background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 18px 45px rgba(15,23,42,.24)" }}>
                    <h3 style={{ margin: "0 0 8px", color: "#0C3B3C" }}>Supprimer les images sélectionnées ?</h3>
                    <p style={{ margin: "0 0 18px", color: "#5A6D68" }}>Cette action supprimera définitivement {selected.size} image{selected.size > 1 ? "s" : ""} du justificatif de livraison.</p>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        <button type="button" onClick={() => setConfirmDelete(false)} disabled={working} style={{ padding: "9px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "white" }}>Annuler</button>
                        <button type="button" onClick={deleteSelected} disabled={working} style={{ padding: "9px 14px", borderRadius: "8px", border: 0, background: "#b91c1c", color: "white", fontWeight: 700 }}>{working ? "Suppression…" : "Confirmer la suppression"}</button>
                    </div>
                </div>
            </div>}
        </dialog>
    );
}
